import { useState, useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { useDebouncedCallback } from "use-debounce";
import { MOBILE_SCREEN } from "../../../components/theme";
import "react-phone-number-input/style.css";
import { useGetCurrentUserQuery } from "../../../api/users";
import { Card } from "../../../components/layout/Card";
import {
  ErrorWrapper,
  InputWrapper,
} from "../questionnaireInputs/SharedFormStyles";
import RequiredStar from "../../../components/RequiredAsterisk";
import { InputField } from "../../../components/forms/InputField";
import { useUpdateUserProfileMutation } from "../../../api/users";
import { capitalizeFirstLetter } from "../../../utils/format";
import { StyledDescription } from "../Questionnaire";

const CardContainer = styled.div`
  margin: 24px 0;
  ${MOBILE_SCREEN} {
    margin: 16px 0;
  }
`;

const userFullNameQuestionConfig = [
  {
    id: "firstName",
    title: "What is your first name?",
    description:
      "<p>Your legal name as listed on your birth certificate, driver's license, or passport.</p>",
    isRequired: true,
    placeholder: "e.g. John",
    alternateTitle: "Confirm your first name",
    alternateDescription:
      "<p>Your legal name as listed on your birth certificate, driver's license, or passport.</p>",
  },
  {
    id: "middleName",
    title: "What is your middle name?",
    description: "<p>Leave blank if not applicable.</p>",
    isRequired: false,
    placeholder: "e.g. Alexander",
    alternateTitle: "What is your middle name?",
    alternateDescription: "<p>Leave blank if not applicable.</p>",
  },
  {
    id: "lastName",
    title: "What is your last name?",
    description: "",
    isRequired: true,
    placeholder: "e.g. Smith",
    alternateTitle: "What is your last name?",
    alternateDescription: "",
  },
  {
    id: "suffix",
    title: "What is your suffix?",
    description: "",
    isRequired: false,
    placeholder: "e.g. Jr, Sr, III, IV",
    alternateTitle: "What is your suffix?",
    alternateDescription: "<p>Leave blank if not applicable.</p>",
  },
];

const UserFullNameForm = ({
  shouldValidateQuestionnaireSection = false,
  sectionKey,
}: {
  shouldValidateQuestionnaireSection?: boolean;
  sectionKey: string;
}) => {
  const { data: user } = useGetCurrentUserQuery();
  const [updateUser] = useUpdateUserProfileMutation();

  // Track whether each field is currently saving to prevent race conditions
  const pendingSaveRef = useRef<Record<string, boolean>>({});
  // Track queued values to save after current save completes
  const pendingValueRef = useRef<Record<string, string | undefined>>({});

  const [userName, setUserName] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    suffix: "",
  });

  // Initialize field status with all configured fields
  const [fieldStatus, setFieldStatus] = useState<
    Record<string, "idle" | "loading" | "success" | "error">
  >({
    firstName: "idle",
    middleName: "idle",
    lastName: "idle",
    suffix: "idle",
  });

  useEffect(() => {
    if (user) {
      setUserName({
        firstName: user.firstName ?? "",
        middleName: user.middleName ?? "",
        lastName: user.lastName ?? "",
        suffix: user.suffix ?? "",
      });
    }
  }, [user]);

  const saveImmediately = (value: string, field: string) => {
    if (!user?.id) return;

    // If already saving this field, queue the latest value for retry
    if (pendingSaveRef.current[field]) {
      pendingValueRef.current[field] = value;
      return;
    }

    pendingSaveRef.current[field] = true;

    void (async () => {
      setFieldStatus((prev) => ({ ...prev, [field]: "loading" }));

      try {
        await updateUser({
          id: user.id,
          [field]: value === "" ? null : value,
        }).unwrap();

        setFieldStatus((prev) => ({ ...prev, [field]: "success" }));
      } catch (error) {
        console.error(`Error updating ${field}:`, error);
        setFieldStatus((prev) => ({ ...prev, [field]: "error" }));
      } finally {
        pendingSaveRef.current[field] = false;

        // If a new value was queued while saving, save it now
        const queuedValue = pendingValueRef.current[field];
        if (queuedValue !== undefined) {
          pendingValueRef.current[field] = undefined;
          saveImmediately(queuedValue, field);
        }
      }
    })();
  };

  const save = useDebouncedCallback(saveImmediately, 500);

  const handleInputChange = (field: string, value: string) => {
    setUserName((prev) => ({ ...prev, [field]: value }));

    // Set field status to idle when values change
    if (fieldStatus[field] === "success" || fieldStatus[field] === "error") {
      setFieldStatus((prev) => ({ ...prev, [field]: "idle" }));
    }
    save(value, field);
  };

  const handleInputBlur = (field: string, value: string) => {
    // Cancel any pending debounced saves to prevent duplicate requests
    save.cancel();

    // Trim the value to remove leading/trailing spaces
    let processedValue = value.trim();

    // Capitalize first letter of name fields for proper document formatting
    if (
      field === "firstName" ||
      field === "middleName" ||
      field === "lastName"
    ) {
      processedValue = capitalizeFirstLetter(processedValue);
    }

    // Update local state if value changed
    if (processedValue !== value) {
      setUserName((prev) => ({ ...prev, [field]: processedValue }));
    }

    // Save immediately on blur. The saveImmediately function serializes updates
    // per field to prevent race conditions where requests arrive out of order
    saveImmediately(processedValue, field);
  };

  return (
    <>
      {userFullNameQuestionConfig.map((question) => {
        if (
          sectionKey === "background_information" &&
          question.id !== "firstName"
        ) {
          return null;
        }
        const fieldValue = userName[question.id as keyof typeof userName];
        const hasNoValue =
          (!fieldValue || fieldValue.trim() === "") && question.isRequired;
        const validationError = !!(
          hasNoValue && shouldValidateQuestionnaireSection
        );

        return (
          <CardContainer key={question.id} id={question.id}>
            <Card
              header={{
                title: (
                  <>
                    {sectionKey === "background_information"
                      ? question.title
                      : question.alternateTitle}
                    {question.isRequired && <RequiredStar />}
                  </>
                ),
              }}
              isError={validationError}
            >
              {sectionKey === "background_information" &&
                question.description && (
                  <StyledDescription
                    dangerouslySetInnerHTML={{
                      __html: question.description,
                    }}
                  />
                )}
              {sectionKey === "understanding" &&
                question.alternateDescription && (
                  <StyledDescription
                    dangerouslySetInnerHTML={{
                      __html: question.alternateDescription,
                    }}
                  />
                )}
              <InputWrapper>
                <InputField
                  type="text"
                  value={userName[question.id as keyof typeof userName]}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleInputChange(question.id, e.target.value);
                  }}
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                    handleInputBlur(question.id, e.target.value);
                  }}
                  dataTestId={`${question.id}-input`}
                  placeholder={question.placeholder}
                />
                {/* Commenting out the loader and success icon for now as per the request of the design team */}
                {/*
                {fieldStatus[question.id] === "loading" && (
                  <IconContainer>
                    <Loader spinnerOnly={true} size={isMobile ? 16 : 34} />
                  </IconContainer>
                )}
                {fieldStatus[question.id] === "success" &&
                  userName[question.id as keyof typeof userName] !== "" && (
                    <IconContainer>
                      <GreenTick size={isMobile ? IconSize.S : IconSize.XL} />
                    </IconContainer>
                  )}
                */}
              </InputWrapper>
              {fieldStatus[question.id] === "error" && (
                <ErrorWrapper>
                  Error saving your information, please refresh the page and try
                  again
                </ErrorWrapper>
              )}
            </Card>
          </CardContainer>
        );
      })}
    </>
  );
};

export default UserFullNameForm;
